// Guest Radar visitor tracker (ADR 004). Runs invisibly for ALL guests: it
// records an anonymous visit, batches app_open events, and heartbeats so the
// admin dashboard and public hit counter have data. It renders NO UI, sends NO
// admin credentials (public endpoints only), and FAILS SILENTLY on any error —
// analytics must never break the OS. Honours a localStorage opt-out.

import { radar } from './api.svelte.js';

const OPTOUT_KEY = 'aquaos.radar.optout';
const VISIT_KEY = 'aquaos.radar.visitId';
const EVENT_FLUSH_MS = 10_000;
const HEARTBEAT_MS = 30_000;

let visitId = null;
let started = false;
// Pending app_open ids awaiting the next flush. A plain array (not a reactive
// Set/Map) per the reactive-file lint rule — it is never rendered.
let pending = [];
let eventTimer = null;
let heartbeatTimer = null;

function optedOut() {
  try {
    return localStorage.getItem(OPTOUT_KEY) === '1';
  } catch {
    return false;
  }
}

function referrerDomain() {
  try {
    if (!document.referrer) return '';
    return new URL(document.referrer).hostname;
  } catch {
    return '';
  }
}

// viewport returns the honest, non-identifying client hints the server cannot
// derive from headers: screen size and UI language. Every access is guarded so
// this is safe under SSR / partial-DOM environments. The server clamps the
// values; blanks/zeros are fine.
function viewport() {
  const out = {};
  try {
    if (typeof screen !== 'undefined') {
      if (Number.isFinite(screen.width)) out.screenW = Math.round(screen.width);
      if (Number.isFinite(screen.height)) out.screenH = Math.round(screen.height);
    }
    if (typeof navigator !== 'undefined' && navigator.language) {
      out.language = navigator.language;
    }
  } catch {
    /* no viewport info available — proceed without it */
  }
  return out;
}

// startVisitorTracking initialises tracking once. Safe to call multiple times;
// only the first call has an effect. Does nothing if the guest has opted out.
export async function startVisitorTracking() {
  if (started || optedOut()) return;
  if (typeof fetch === 'undefined' || typeof document === 'undefined') return;
  started = true;

  // Reuse a visit id from this browser session if present (a reload should not
  // start a new session); otherwise ask the server to start one.
  try {
    const cached = sessionStorage.getItem(VISIT_KEY);
    if (cached) visitId = cached;
  } catch {
    /* sessionStorage unavailable — proceed without a cache */
  }

  if (!visitId) {
    const res = await radar.visit({
      referrer: referrerDomain(),
      entryPath: location?.pathname ?? '/',
      ...viewport(),
    });
    if (res?.visitId) {
      visitId = res.visitId;
      try {
        sessionStorage.setItem(VISIT_KEY, visitId);
      } catch {
        /* ignore */
      }
    }
  }
  if (!visitId) return; // server unavailable — give up quietly

  eventTimer = setInterval(flushEvents, EVENT_FLUSH_MS);
  heartbeatTimer = setInterval(() => {
    radar.heartbeat({ visitId });
  }, HEARTBEAT_MS);

  // Final heartbeat on page hide, preferring sendBeacon so it survives unload.
  if (typeof window !== 'undefined') {
    window.addEventListener('pagehide', finalHeartbeat);
  }
}

// trackAppOpen records an app_open. Queued and flushed in batches to spare the
// network. No-op when tracking is inactive or the guest opted out.
export function trackAppOpen(appId) {
  if (!started || !visitId || !appId) return;
  pending.push(appId);
}

function flushEvents() {
  if (!visitId || pending.length === 0) return;
  const batch = pending;
  pending = [];
  for (const app of batch) {
    radar.event({ visitId, app });
  }
}

function finalHeartbeat() {
  if (!visitId) return;
  flushEvents();
  try {
    const url = import.meta.env.VITE_API_URL ?? 'http://localhost:8080';
    if (navigator?.sendBeacon) {
      const blob = new Blob([JSON.stringify({ visitId })], { type: 'application/json' });
      navigator.sendBeacon(`${url}/api/ioc/radar/heartbeat`, blob);
      return;
    }
  } catch {
    /* fall through to fetch */
  }
  radar.heartbeat({ visitId });
}

// stopVisitorTracking tears down timers/listeners (used in tests and on OS
// shutdown). Idempotent.
export function stopVisitorTracking() {
  if (eventTimer) clearInterval(eventTimer);
  if (heartbeatTimer) clearInterval(heartbeatTimer);
  eventTimer = null;
  heartbeatTimer = null;
  if (typeof window !== 'undefined') {
    window.removeEventListener('pagehide', finalHeartbeat);
  }
  started = false;
}
